import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Heart, Trash2, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const FavoritesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const data = await api.getFavorites();
      setFavorites(data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Failed to load favorites');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (favoriteId) => {
    try {
      await api.deleteFavorite(favoriteId);
      setFavorites(favorites.filter(f => f.id !== favoriteId));
      toast.success('Removed from favorites');
    } catch (error) {
      console.error('Error deleting favorite:', error);
      toast.error('Failed to remove favorite');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="favorites-back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold text-pink-600" data-testid="favorites-title">
                  Saved Favorites
                </h1>
                <p className="text-sm text-slate-500">{favorites.length} questions saved</p>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="text-center py-16" data-testid="favorites-loading">
            <Loader2 className="w-16 h-16 animate-spin text-pink-500 mx-auto mb-4" />
            <p className="text-lg text-slate-600">Loading your favorites...</p>
          </div>
        ) : favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
            data-testid="favorites-empty"
          >
            <Heart className="w-24 h-24 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-slate-800 mb-2">No favorites yet</h2>
            <p className="text-slate-600 mb-6">Save questions while practicing to review them later!</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-pink-500 hover:bg-pink-600 text-white rounded-full px-8 py-6 text-lg font-bold tactile-button border-pink-700"
              data-testid="go-to-dashboard-button"
            >
              Start Practicing
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {favorites.map((favorite, index) => {
              const question = favorite.question_data;
              return (
                <motion.div
                  key={favorite.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="p-6 rounded-3xl border-2 border-slate-200" data-testid={`favorite-card-${index}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-xs font-bold" data-testid={`favorite-topic-${index}`}>
                            {question.topic}
                          </div>
                          <div className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-xs font-bold">
                            {question.level}
                          </div>
                        </div>
                        
                        <p className="text-slate-800 mb-3 whitespace-pre-wrap" data-testid={`favorite-question-${index}`}>
                          {question.question_text}
                        </p>
                        
                        {question.options && (
                          <div className="space-y-2 mb-3">
                            {question.options.map((option, optIndex) => (
                              <div key={optIndex} className="text-sm p-2 bg-slate-50 rounded-lg text-slate-600">
                                {option}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                          <p className="text-xs font-bold text-emerald-700 mb-1">Correct Answer:</p>
                          <p className="text-sm text-slate-800">{question.correct_answer}</p>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => handleDelete(favorite.id)}
                        variant="outline"
                        size="sm"
                        className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`delete-favorite-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default FavoritesPage;